import { useEffect, useState } from "react";
import { api, formatApiErrorDetail, fmtDate } from "@/lib/api";
import { PageHeader, Section, Pill } from "@/components/ui-kit";
import { toast } from "sonner";
import { Plus, Pencil, FileText, Trash2 } from "lucide-react";

const blankForm = { name: "", email: "", phone: "", type: "INDIVIDUAL", bvn: "", nin: "", address: "" };
const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function CustomersPage() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(blankForm);
  const [editing, setEditing] = useState(null); // existing customer being edited

  const load = () => api.get("/customers").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);

  const onTypeChange = (t) => {
    setForm((f) => ({ ...f, type: t, bvn: t === "INDIVIDUAL" ? "" : f.bvn, nin: t === "CORPORATE" ? "" : f.nin }));
  };

  const submitCreate = async (e) => {
    e.preventDefault();
    if (form.type === "INDIVIDUAL" && (!/^\d{11}$/.test(form.nin || ""))) return toast.error("NIN must be exactly 11 numeric characters.");
    if (form.type === "CORPORATE" && (!/^\d{11}$/.test(form.bvn || ""))) return toast.error("BVN must be exactly 11 numeric characters.");
    try {
      const payload = { ...form,
        bvn: form.type === "CORPORATE" ? form.bvn : null,
        nin: form.type === "INDIVIDUAL" ? form.nin : null };
      await api.post("/customers", payload);
      toast.success("Customer added");
      setOpen(false); setForm(blankForm); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    try {
      // KYC fields are immutable — only send mutable ones
      const payload = {
        name: editing.name, email: editing.email,
        phone: editing.phone || null, address: editing.address || null,
        status: editing.status,
      };
      const { data } = await api.patch(`/customers/${editing.id}`, payload);
      toast.success("Customer updated");
      setEditing(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const inputCls = "w-full bg-white border border-border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary text-sm";
  const readOnlyCls = inputCls + " bg-muted text-muted-foreground cursor-not-allowed";

  return (
    <div data-testid="customers-page">
      <PageHeader title="Customers" subtitle="Customer book"
        actions={<button onClick={() => { setOpen(true); setEditing(null); }} className="btn-primary" data-testid="new-customer-btn"><Plus className="w-4 h-4" /> New customer</button>}
      />

      {open && (
        <Section title="Add customer" testId="new-customer-form" actions={<button onClick={() => { setOpen(false); setForm(blankForm); }} className="btn-ghost">Cancel</button>}>
          <form onSubmit={submitCreate} className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="label block mb-1">Customer Type *</label>
              <select required className={inputCls} value={form.type}
                      onChange={(e) => onTypeChange(e.target.value)} data-testid="customer-type">
                <option value="INDIVIDUAL">Individual</option>
                <option value="CORPORATE">Corporate</option>
              </select>
            </div>
            <div><label className="label block mb-1">Name</label><input required className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="customer-name" /></div>
            <div><label className="label block mb-1">Email</label><input required type="email" className={inputCls} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="customer-email" /></div>
            <div><label className="label block mb-1">Phone</label><input className={inputCls} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="customer-phone" /></div>
            {form.type === "INDIVIDUAL" ? (
              <div>
                <label className="label block mb-1">NIN * (11 digits)</label>
                <input required inputMode="numeric" pattern="\d{11}" maxLength={11} className={inputCls} value={form.nin}
                       onChange={(e) => setForm({ ...form, nin: e.target.value.replace(/\D/g, "").slice(0, 11) })}
                       data-testid="customer-nin" placeholder="11-digit NIN" />
              </div>
            ) : (
              <div>
                <label className="label block mb-1">BVN * (11 digits)</label>
                <input required inputMode="numeric" pattern="\d{11}" maxLength={11} className={inputCls} value={form.bvn}
                       onChange={(e) => setForm({ ...form, bvn: e.target.value.replace(/\D/g, "").slice(0, 11) })}
                       data-testid="customer-bvn" placeholder="11-digit BVN" />
              </div>
            )}
            <div className="md:col-span-2"><label className="label block mb-1">Address</label><input className={inputCls} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="customer-address" /></div>
            <div className="md:col-span-2 flex justify-end"><button type="submit" className="btn-primary" data-testid="customer-submit-btn">Save customer</button></div>
          </form>
        </Section>
      )}

      {editing && (
        <Section title={`Edit · ${editing.name}`} testId="edit-customer-form" actions={<button onClick={() => setEditing(null)} className="btn-ghost">Cancel</button>}>
          <form onSubmit={submitEdit} className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="label block mb-1">Customer Type (read-only)</label>
              <input readOnly className={readOnlyCls} value={editing.type} data-testid="edit-type-readonly" />
            </div>
            <div>
              <label className="label block mb-1">{editing.type === "INDIVIDUAL" ? "NIN (read-only)" : "BVN (read-only)"}</label>
              <input readOnly className={readOnlyCls}
                     value={editing.type === "INDIVIDUAL" ? (editing.nin || "—") : (editing.bvn || "—")}
                     data-testid="edit-id-readonly" />
              <div className="text-xs text-muted-foreground mt-1">KYC IDs are immutable. Contact compliance to change.</div>
            </div>
            <div><label className="label block mb-1">Name</label><input required className={inputCls} value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="edit-name" /></div>
            <div><label className="label block mb-1">Email</label><input required type="email" className={inputCls} value={editing.email} onChange={(e) => setEditing({ ...editing, email: e.target.value })} data-testid="edit-email" /></div>
            <div><label className="label block mb-1">Phone</label><input className={inputCls} value={editing.phone || ""} onChange={(e) => setEditing({ ...editing, phone: e.target.value })} data-testid="edit-phone" /></div>
            <div>
              <label className="label block mb-1">Status</label>
              <select className={inputCls} value={editing.status} onChange={(e) => setEditing({ ...editing, status: e.target.value })} data-testid="edit-status">
                <option value="ACTIVE">Active</option>
                <option value="INACTIVE">Inactive</option>
              </select>
            </div>
            <div className="md:col-span-2"><label className="label block mb-1">Address</label><input className={inputCls} value={editing.address || ""} onChange={(e) => setEditing({ ...editing, address: e.target.value })} data-testid="edit-address" /></div>
            <div className="md:col-span-2 flex justify-end gap-2">
              <a className="btn-ghost text-xs" target="_blank" rel="noreferrer"
                 href={`${BACKEND}/api/reports/customer-statement/${editing.id}?format=pdf`}
                 data-testid="customer-statement-btn">
                <FileText className="w-3.5 h-3.5" /> Statement PDF
              </a>
              <button type="submit" className="btn-primary" data-testid="edit-submit-btn">Save changes</button>
            </div>
          </form>
        </Section>
      )}

      <Section title={`All customers (${items.length})`} testId="customers-list">
        {items.length === 0 ? (
          <div className="text-sm text-muted-foreground py-6 text-center" data-testid="empty-customers">No customers yet.</div>
        ) : (
          <table className="w-full text-sm">
            <thead><tr className="text-left">
              <th className="label py-2">Name</th><th className="label py-2">Email</th><th className="label py-2">Phone</th>
              <th className="label py-2">Type</th><th className="label py-2">ID</th>
              <th className="label py-2">Status</th><th className="label py-2">Joined</th>
              <th className="label py-2 text-right">Actions</th>
            </tr></thead>
            <tbody>
              {items.map((c) => (
                <tr key={c.id} className="border-t border-border">
                  <td className="py-2.5 font-medium">{c.name}</td>
                  <td className="py-2.5">{c.email}</td>
                  <td className="py-2.5">{c.phone || "—"}</td>
                  <td className="py-2.5">{c.type}</td>
                  <td className="py-2.5 font-mono text-xs">
                    {c.type === "INDIVIDUAL"
                      ? (c.nin ? `NIN ${c.nin.slice(0, 3)}•••${c.nin.slice(-2)}` : "—")
                      : (c.bvn ? `BVN ${c.bvn.slice(0, 3)}•••${c.bvn.slice(-2)}` : "—")}
                  </td>
                  <td className="py-2.5"><Pill variant={c.status === "ACTIVE" ? "success" : "default"}>{c.status}</Pill></td>
                  <td className="py-2.5">{fmtDate(c.created_at)}</td>
                  <td className="py-2.5 text-right">
                    <div className="inline-flex gap-1">
                      <button className="btn-ghost px-2 py-1 text-xs" onClick={() => { setEditing({ ...c }); setOpen(false); }} data-testid={`edit-customer-${c.id}`}>
                        <Pencil className="w-3 h-3" /> Edit
                      </button>
                      <button className="btn-ghost px-2 py-1 text-xs text-secondary" data-testid={`delete-customer-${c.id}`}
                        onClick={async () => {
                          if (!window.confirm(`Delete ${c.name}?\nIf they have linked savings/loans, the customer will be deactivated instead.`)) return;
                          try {
                            const { data } = await api.delete(`/customers/${c.id}`);
                            toast.success(data.mode === "soft" ? "Deactivated (had linked records)" : "Customer deleted");
                            load();
                          } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
                        }}>
                        <Trash2 className="w-3 h-3" /> Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>
    </div>
  );
}
